import { createContext, useContext, useMemo } from 'react';
import { useSnowflakeJoinMetadata } from 'reporting-snowflake/relational/metadata/joins-with-meta';
import { useTableDescriptionMeta } from 'reporting-snowflake/relational/metadata/table-description-meta';
import { createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
import { useSnowflakeReportUpdater } from 'reporting-snowflake/relational/updaters/report/use-snowflake-updater';
import { getReportColumnsFromRelationalReport } from '../lib/relational-report-utils/property-utils';
import { useDatasetProperties } from '../hooks/relationalReports/useDatasetProperties';
import { useDefaultProperties } from '../hooks/relationalReports/useDefaultProperties';
import { useFormulaFieldProperties } from '../hooks/relationalReports/useFormulaFieldProperties';
import { useTopProperties } from '../hooks/relationalReports/useTopProperties';
import { useProperties } from '../hooks/relationalReports/useProperties';
import { removeDuplicateProperties, initialPropertiesByTable, filterOptionGroupsByAvailableColumns } from '../lib/relational-report-utils/property-utils';
import { jsx as _jsx } from "react/jsx-runtime";
const RelationalReportDataContext = /*#__PURE__*/createContext(undefined);
const RelationalReportInfrastructureContext = /*#__PURE__*/createContext(undefined);
const RelationalReportPropertiesContext = /*#__PURE__*/createContext(undefined);
const RelationalReportAdvancedPropertiesContext = /*#__PURE__*/createContext(undefined);
export const RelationalReportProvider = ({
  report,
  children
}) => {
  const relationalReport = useMemo(() => createRelationalReportFromBaseReport(report), [report]);
  const reportColumns = useMemo(() => getReportColumnsFromRelationalReport(relationalReport), [relationalReport]);
  const validPropertiesByTable = useMemo(() => initialPropertiesByTable(reportColumns.allAvailableColumns), [reportColumns.allAvailableColumns]);
  const snowflakeJoinMetadata = useSnowflakeJoinMetadata();
  const {
    data: allJoinMetadata,
    loading: isJoinMetadataLoading
  } = snowflakeJoinMetadata;
  const fullTableAndPropertyMeta = useTableDescriptionMeta(relationalReport.table);
  const snowflakeUpdater = useSnowflakeReportUpdater(relationalReport);
  const {
    isTopPropertiesLoading = true,
    allTopProperties
  } = useTopProperties({
    relationalReport,
    allJoinMetadata,
    fullTableAndPropertyMeta,
    allAvailableColumns: reportColumns.allAvailableColumns
  });
  const {
    isDefaultPropertiesLoading = true,
    allDefaultExpressions = [],
    availableDefaultExpressions = [],
    allDefaultPropertyData = [],
    availableDefaultPropertyData = [],
    allDefaultPropertiesGroup = [],
    availableDefaultPropertiesGroup = []
  } = useDefaultProperties({
    relationalReport,
    allJoinMetadata,
    fullTableAndPropertyMeta,
    allAvailableColumns: reportColumns.allAvailableColumns
  });
  const {
    isPropertiesLoading = true,
    standardProperties
  } = useProperties({
    relationalReport,
    allJoinMetadata,
    fullTableAndPropertyMeta
  });
  const dedupedAllStandardProperties = removeDuplicateProperties(allTopProperties, standardProperties);
  const {
    isFormulaFieldPropertiesLoading = true,
    formulaFieldProperties = [],
    formulaFieldExpressions = []
  } = useFormulaFieldProperties({
    expressions: relationalReport.expressions,
    allAvailableColumns: reportColumns.allAvailableColumns
  });
  const {
    isDatasetPropertiesLoading = true,
    allDatasetProperties = [],
    allDatasetFields = null
  } = useDatasetProperties({
    relationalReport
  });
  const isPropertyOptionsLoading = isTopPropertiesLoading || isDefaultPropertiesLoading || isPropertiesLoading || isFormulaFieldPropertiesLoading || isDatasetPropertiesLoading;
  const isLoading = useMemo(() => isJoinMetadataLoading || fullTableAndPropertyMeta.loading || isPropertyOptionsLoading, [isJoinMetadataLoading, fullTableAndPropertyMeta.loading, isPropertyOptionsLoading]);
  const computedPropertyOptions = useMemo(() => {
    if (isLoading) {
      return {
        allPropertiesOptions: [],
        availablePropertiesOptions: [],
        availablePropertiesOptionsWithoutDefaultMeasures: []
      };
    }
    const availableTopProperties = filterOptionGroupsByAvailableColumns(allTopProperties, validPropertiesByTable);
    const availableStandardProperties = filterOptionGroupsByAvailableColumns(standardProperties, validPropertiesByTable);
    const dedupedAvailableStandardProperties = removeDuplicateProperties(availableTopProperties, availableStandardProperties);
    const availableDatasetProperties = filterOptionGroupsByAvailableColumns(allDatasetProperties, validPropertiesByTable);
    const allPropertiesOptions = [...allTopProperties, ...allDefaultPropertiesGroup, ...dedupedAllStandardProperties, ...formulaFieldProperties, ...allDatasetProperties];
    const availablePropertiesOptions = [...availableTopProperties, ...availableDefaultPropertiesGroup, ...dedupedAvailableStandardProperties, ...formulaFieldProperties, ...availableDatasetProperties];
    const availablePropertiesOptionsWithoutDefaultMeasures = [...availableTopProperties, ...dedupedAvailableStandardProperties, ...availableDatasetProperties.filter(group => group.value !== 'Dataset fields' && group.value !== 'Dataset measures')];
    return {
      allPropertiesOptions,
      availablePropertiesOptions,
      availablePropertiesOptionsWithoutDefaultMeasures
    };
  }, [isLoading, allTopProperties, allDefaultPropertiesGroup, dedupedAllStandardProperties, formulaFieldProperties, allDatasetProperties, standardProperties, validPropertiesByTable, availableDefaultPropertiesGroup]);
  const dataContextValue = useMemo(() => ({
    report,
    relationalReport,
    reportColumns,
    isLoading
  }), [report, relationalReport, reportColumns, isLoading]);
  const infrastructureContextValue = useMemo(() => ({
    snowflakeJoinMetadata,
    fullTableAndPropertyMeta,
    snowflakeUpdater
  }), [snowflakeJoinMetadata, fullTableAndPropertyMeta, snowflakeUpdater]);
  const propertiesContextValue = useMemo(() => ({
    allPropertiesOptions: computedPropertyOptions.allPropertiesOptions,
    availablePropertiesOptions: computedPropertyOptions.availablePropertiesOptions,
    availablePropertiesOptionsWithoutDefaultMeasures: computedPropertyOptions.availablePropertiesOptionsWithoutDefaultMeasures
  }), [computedPropertyOptions]);
  const advancedPropertiesContextValue = useMemo(() => ({
    allDefaultExpressions,
    allDefaultPropertyData,
    availableDefaultExpressions,
    availableDefaultPropertyData,
    formulaFieldExpressions,
    allDatasetFields
  }), [allDefaultExpressions, allDefaultPropertyData, availableDefaultExpressions, availableDefaultPropertyData, formulaFieldExpressions, allDatasetFields]);
  return /*#__PURE__*/_jsx(RelationalReportDataContext.Provider, {
    value: dataContextValue,
    children: /*#__PURE__*/_jsx(RelationalReportInfrastructureContext.Provider, {
      value: infrastructureContextValue,
      children: /*#__PURE__*/_jsx(RelationalReportPropertiesContext.Provider, {
        value: propertiesContextValue,
        children: /*#__PURE__*/_jsx(RelationalReportAdvancedPropertiesContext.Provider, {
          value: advancedPropertiesContextValue,
          children: children
        })
      })
    })
  });
};

// Individual context hooks
export const useRelationalReportData = () => {
  const context = useContext(RelationalReportDataContext);
  if (!context) {
    throw new Error('useRelationalReportData must be used within a RelationalReportProvider');
  }
  return context;
};
export const useRelationalReportInfrastructure = () => {
  const context = useContext(RelationalReportInfrastructureContext);
  if (!context) {
    throw new Error('useRelationalReportInfrastructure must be used within a RelationalReportProvider');
  }
  return context;
};
export const useRelationalReportProperties = () => {
  const context = useContext(RelationalReportPropertiesContext);
  if (!context) {
    throw new Error('useRelationalReportProperties must be used within a RelationalReportProvider');
  }
  return context;
};
export const useRelationalReportAdvancedProperties = () => {
  const context = useContext(RelationalReportAdvancedPropertiesContext);
  if (!context) {
    throw new Error('useRelationalReportAdvancedProperties must be used within a RelationalReportProvider');
  }
  return context;
};